import {
  CUSTOM_ICON_DARK_DATA_SET,
  CUSTOM_ICON_LIGHT_DATA_SET,
  CUSTOM_SHAPE_DATA_SET,
} from "@circle-react-uikit/EmojiPickerV3/data";
import noop from "lodash/noop";
import { createContext, useContext, useState } from "react";
import type { PropsWithChildren } from "react";

import { TAB_ITEMS } from "./constants";
import type { TabItem } from "./constants";
import type { EmojiPickerV4Props } from "./types";

interface EmojiPickerV4ContextValue extends Omit<
  EmojiPickerV4Props,
  "pickerAddOns"
> {
  activeTab: TabItem;
  enabledTabs: TabItem[];
  isCustomFileUploadProcessing: boolean;
  setActiveTab: (tab: TabItem) => void;
  setIsCustomFileUploadProcessing: (v: boolean) => void;
}

const EmojiPickerV4Context = createContext<EmojiPickerV4ContextValue | null>(
  null,
);
EmojiPickerV4Context.displayName = "EmojiPickerV4Context";

export const EmojiPickerV4Provider = ({
  children,
  pickerAddOns = [],
  customShapeIcons = CUSTOM_SHAPE_DATA_SET,
  customIcons = CUSTOM_ICON_LIGHT_DATA_SET,
  customIconsDark = CUSTOM_ICON_DARK_DATA_SET,
  handleCustomIconUpload = noop,
  onEmojiSelect = noop,
  onRemoveEmoji,
  shouldCloseOnSelect = true,
  shouldShowSkinTonePicker = true,
  ...rest
}: PropsWithChildren<EmojiPickerV4Props>) => {
  const enabledTabs = TAB_ITEMS.filter(
    tab => tab.id === "emoji" || pickerAddOns.includes(tab.id),
  );
  const [activeTab, setActiveTab] = useState<TabItem>(enabledTabs[0]);
  const [isCustomFileUploadProcessing, setIsCustomFileUploadProcessing] =
    useState(false);

  return (
    <EmojiPickerV4Context.Provider
      value={{
        ...rest,
        customShapeIcons,
        customIcons,
        customIconsDark,
        handleCustomIconUpload,
        onEmojiSelect,
        onRemoveEmoji,
        shouldCloseOnSelect,
        shouldShowSkinTonePicker,
        activeTab,
        setActiveTab,
        enabledTabs,
        isCustomFileUploadProcessing,
        setIsCustomFileUploadProcessing,
      }}
    >
      {children}
    </EmojiPickerV4Context.Provider>
  );
};

export const useEmojiPickerV4Context = () => {
  const ctx = useContext(EmojiPickerV4Context);
  if (!ctx) throw new Error("useEmojiPickerV4Context used outside provider");
  return ctx;
};
